import { useCallback, useEffect, useRef, useState } from 'react'

import dayjs from 'dayjs'
import { useCookie } from 'react-use'

import { ProfileSectionContainer_UserFragment } from '@web/federation/gql/graphql'
import useOutsideClick from '@web/hooks/useOutsideClick'
import { Nullish } from '@web/types'

const TOOLTIP_SHOW_DURATION = 5000

const useProfileEditTooltipHide = (idno?: string) => {
  const [_showCount, setShowCount] = useCookie(`profile-tooltip-show-count:${idno}`)

  const showCount = Number(_showCount) || 0

  return {
    shouldHideTooltip: showCount >= 3,
    hideTooltip: () => {
      if (!idno) return
      setShowCount((showCount + 1).toString(), { expires: dayjs().add(1, 'year').toDate() })
    },
  }
}

export const useProfileTooltip = (user: Nullish<ProfileSectionContainer_UserFragment>) => {
  const [showTooltip, setShowTooltip] = useState(false)

  const { shouldHideTooltip, hideTooltip } = useProfileEditTooltipHide(user?.idno)

  const hideTooltipRef = useRef<() => void>()
  hideTooltipRef.current = hideTooltip

  const tooltipRef = useRef<HTMLDivElement>(null)

  const closeTooltip = useCallback(() => {
    setShowTooltip(showTooltip => {
      if (showTooltip) hideTooltipRef.current?.()
      return false
    })
  }, [])

  useOutsideClick({ innerRef: tooltipRef, callback: closeTooltip, useHooks: showTooltip })

  useEffect(() => {
    setShowTooltip(!user?.profile?.updated && !shouldHideTooltip)
  }, [user, shouldHideTooltip])

  // 스크롤 시에 꺼져야한다.
  useEffect(() => {
    window.addEventListener('scroll', closeTooltip)
    return () => window.removeEventListener('scroll', closeTooltip)
  }, [])

  // 다른 영향이 없을 때 5초 뒤에 꺼져야 한다.
  useEffect(() => {
    let timeout: number

    if (showTooltip) {
      timeout = window.setTimeout(closeTooltip, TOOLTIP_SHOW_DURATION)
    }

    return () => {
      if (timeout) window.clearTimeout(timeout)
    }
  }, [showTooltip])

  return { showTooltip, tooltipRef }
}
